/**
 * Axios instance for the admin dashboard. Attaches the in-memory access token, unwraps
 * the { data, meta, error } envelope, and transparently refreshes on a 401 (single
 * flight — concurrent 401s share one refresh call), retrying the original request once.
 */
import axios from 'axios';

const baseURL = import.meta.env.VITE_API_BASE_URL || '/api';

export const apiClient = axios.create({
  baseURL,
  withCredentials: true, // sends the httpOnly refresh cookie to /auth/refresh
  timeout: 20000,
  headers: { 'Content-Type': 'application/json' },
});

// ── Auth token ────────────────────────────────────────────────────────────────
// Persisted to localStorage so a page reload keeps the session for the full token
// lifetime (~30 days) without depending on the cookie refresh (which fails across
// origins in production). Trade-off: localStorage is XSS-exposed — acceptable here given
// the long-session requirement; tighten with CSP if needed.
const TOKEN_KEY = 'usmehar.admin.at';
let accessToken = (() => {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
})();
let onAuthLost = null; // callback when refresh fails (force logout)

export function setAccessToken(token) {
  accessToken = token || null;
  try {
    if (token) localStorage.setItem(TOKEN_KEY, token);
    else localStorage.removeItem(TOKEN_KEY);
  } catch {
    /* storage unavailable — keep in memory */
  }
}
export function getAccessToken() {
  return accessToken;
}
export function setOnAuthLost(fn) {
  onAuthLost = fn;
}

apiClient.interceptors.request.use((config) => {
  if (accessToken) config.headers.Authorization = `Bearer ${accessToken}`;
  return config;
});

// ── Single-flight refresh ─────────────────────────────────────────────────────
let refreshing = null;

async function refreshToken() {
  if (!refreshing) {
    refreshing = axios
      .post(`${baseURL}/auth/refresh`, {}, { withCredentials: true })
      .then((res) => {
        const token = res.data?.data?.accessToken;
        setAccessToken(token);
        return token;
      })
      .finally(() => {
        refreshing = null;
      });
  }
  return refreshing;
}

apiClient.interceptors.response.use(
  (response) => response.data,
  async (error) => {
    const original = error.config;
    const status = error.response?.status;

    // Attempt one transparent refresh on 401 (but never for the refresh call itself).
    if (status === 401 && original && !original._retried && !original.url?.includes('/auth/')) {
      original._retried = true;
      try {
        const token = await refreshToken();
        if (token) {
          original.headers.Authorization = `Bearer ${token}`;
          return apiClient(original);
        }
      } catch {
        /* fall through to logout */
      }
      onAuthLost?.();
    }

    const envelope = error.response?.data;
    return Promise.reject({
      status: status ?? 0,
      code: envelope?.error?.code || (error.code === 'ECONNABORTED' ? 'TIMEOUT' : 'NETWORK_ERROR'),
      message:
        envelope?.error?.message ||
        (error.response ? 'Request failed' : 'Could not reach the server.'),
      details: envelope?.error?.details,
      requestId: envelope?.meta?.requestId,
    });
  },
);

export const api = {
  get: async (url, config) => (await apiClient.get(url, config)).data,
  getEnvelope: (url, config) => apiClient.get(url, config),
  post: async (url, body, config) => (await apiClient.post(url, body, config)).data,
  patch: async (url, body, config) => (await apiClient.patch(url, body, config)).data,
  put: async (url, body, config) => (await apiClient.put(url, body, config)).data,
  del: async (url, config) => (await apiClient.delete(url, config)).data,
};

export default apiClient;
